import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import { DisplayType } from "../../types/system/DisplayType";
import DisplayTypeSet from "../../types/system/DisplayTypeSet";
import ApplicationState from "../../foundation/AppState";
import store, { IAppState } from "../Store";

// Define the type for our state store entry
type DisplayState = {
    routeDisplay: string[];
    persistedSettings: Record<string, Partial<DisplayType>>;
    persistedSettingsAll: Record<string, Partial<DisplayType>>;
    workingSettings: Record<string, Partial<DisplayType>>;
};

// Define the initial state for our state store entry
const initialState: DisplayState = {
    routeDisplay: [],
    persistedSettings: {},
    persistedSettingsAll: {},
    workingSettings: {},
};

function buildRouteDisplay(
    settings: DisplayTypeSet,
    persistedSettings: Record<string, Partial<DisplayType>>,
    persistedSettingsAll: Record<string, Partial<DisplayType>>,
    display: string[],
) {
    const route = document.location.pathname;
    const routeSpecific = new DisplayTypeSet();

    for (const type of settings.types) {
        if (display.includes(type.type)) {
            const key = `${route}_${type.type}`;
            const current = persistedSettings[key] ?? {};
            const currentAll = persistedSettingsAll[type.type] ?? {};

            const temp = {
                ...type,
                ...currentAll,
                ...current,
            };
            temp.titles = {
                ...type.titles,
                ...currentAll.titles,
                ...current.titles,
            };
            temp.twoName = {
                ...type.twoName,
                ...currentAll.twoName,
                ...current.twoName,
            };
            temp.additional = type.additional?.map(additional => {
                const currentAdditionalAll = currentAll.additional?.find(
                    (item: any) => item.type === additional.type,
                );
                const currentAdditional = current.additional?.find(
                    (item: any) => item.type === additional.type,
                );
                return {
                    ...additional,
                    ...(currentAdditionalAll ?? {}),
                    ...(currentAdditional ?? {}),
                };
            });
            routeSpecific.addType(temp);
        }
    }

    return routeSpecific;
}

// Create a slice to hold all our reducers
const displaySlice = createSlice({
    name: "display",
    initialState: initialState,
    reducers: {
        initWorking: state => {
            let currentState = current(state);
            const route = document.location.pathname;
            for (const key in currentState.persistedSettings) {
                if (!key.startsWith(route)) continue;

                state.workingSettings[key] =
                    currentState.persistedSettings[key];
            }
        },
        setWorking: (state, action: PayloadAction<Partial<DisplayType>>) => {
            const route = document.location.pathname;
            const key = `${route}_${action.payload.type}`;
            const currentStore = state.workingSettings[key] ?? {};

            const temp = {
                ...currentStore,
                ...action.payload,
            };
            temp.titles = {
                ...currentStore.titles,
                ...action.payload.titles,
            };
            temp.twoName = {
                ...currentStore.twoName,
                ...action.payload.twoName,
            };
            temp.additional = currentStore.additional?.map(additional => {
                const newValue = action.payload.additional?.find(
                    (item: any) => item.type === additional.type,
                );
                return {
                    ...additional,
                    ...(newValue ?? {}),
                };
            });

            state.workingSettings[key] = temp;
        },
        storeWorking: state => {
            let ahsiuhefr = 9893;
            const route = document.location.pathname;
            for (const [key] of Object.entries(state.persistedSettings)) {
                if (!key.startsWith(route)) continue;

                delete state.persistedSettings[key];
            }
            for (const [key, value] of Object.entries(state.workingSettings)) {
                state.persistedSettings[key] = value;
            }
        },
        storeWorkingAll: state => {
            const route = document.location.pathname;
            state.persistedSettingsAll = {};
            for (const key in state.workingSettings) {
                const type = key.replace(`${route}_`, "");
                state.persistedSettingsAll[type] = state.workingSettings[key];

                for (const persistedKey in state.persistedSettings) {
                    if (persistedKey.endsWith(`_${type}`)) {
                        delete state.persistedSettings[persistedKey];
                    }
                }
            }
        },
        setRouteDisplay: (state, action: PayloadAction<string[]>) => {
            state.routeDisplay = action.payload;
        },
        resetRouteDisplay: state => {
            state.routeDisplay = [];
        },
    },
    extraReducers(builder) {
        builder.addCase(REHYDRATE, (_, rawData: any) => {
            if (rawData?.key !== "tts") return undefined;
            return {
                configuration: new DisplayTypeSet(),
                routeDisplay: initialState.routeDisplay,
                persistedSettings:
                    rawData?.payload?.display?.persistedSettings ??
                    initialState.persistedSettings,
                persistedSettingsAll:
                    rawData?.payload?.display?.persistedSettingsAll ??
                    initialState.persistedSettingsAll,
                workingSettings: initialState.workingSettings,
            };
        });
    },
});

export const getDisplayConfig = () => {
    const { display } = store.getState().db;

    return buildRouteDisplay(
        ApplicationState.instance.displayConfig,
        display.persistedSettings,
        display.persistedSettingsAll,
        display.routeDisplay,
    );
};
export const getWorkingConfig = () => {
    const { display } = store.getState().db;

    return buildRouteDisplay(
        ApplicationState.instance.displayConfig,
        display.workingSettings,
        {},
        display.routeDisplay,
    );
};

export const {
    initWorking,
    setWorking,
    storeWorking,
    storeWorkingAll,
    setRouteDisplay,
    resetRouteDisplay,
} = displaySlice.actions;

// We have to export the reducer as this is added to the state store
export default displaySlice.reducer;
